import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { 
  getUserFamilyInfo, 
  getFamilyMembers, 
  createFamily, 
  sendFamilyInvitation, 
  getFamilyInvitations, 
  getSentInvitations,
  acceptFamilyInvitation, 
  declineFamilyInvitation,
  addCustomParticipantToFamily,
  removeCustomParticipantFromFamily,
  markCustomParticipantAsInvited,
  removeFamilyMember,
  FAMILY_ROLES 
} from '../services/familyService';
import { useFamilyParticipants } from '../hooks/useFamilyParticipants';
import { useFamilyRole } from '../hooks/useFamilyRole';
import { participantEvents } from '../utils/participantEvents';

function FamilySharePage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { participants: familyParticipants, refreshParticipants } = useFamilyParticipants();
  const { familyRole, isParent, canEditEvents } = useFamilyRole();
  const [familyInfo, setFamilyInfo] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [sentInvitations, setSentInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Invitation form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState(FAMILY_ROLES.CHILD);
  const [isInviting, setIsInviting] = useState(false);

  // Participant editor state
  const [showParticipantEditor, setShowParticipantEditor] = useState(false);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  
  // Connect to user modal state
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [participantToConnect, setParticipantToConnect] = useState(null);
  const [connectModalPermissions, setConnectModalPermissions] = useState({
    canViewAllEvents: false,
    canEditEvents: false
  });
  
  // Add participant state
  const [newParticipantRole, setNewParticipantRole] = useState(FAMILY_ROLES.CHILD);
  
  // Permission editor state
  const [showPermissionEditor, setShowPermissionEditor] = useState(false);
  const [memberToEditPermissions, setMemberToEditPermissions] = useState(null);
  
  // Expandable user cards state
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  
  // Local toggle states for immediate UI feedback
  const [localPermissions, setLocalPermissions] = useState({});
  
  // Helper function to get current permission value (local override or original)
  const getCurrentPermission = (userId, permissionKey, originalValue) => {
    const localValue = localPermissions[userId]?.[permissionKey];
    return localValue !== undefined ? localValue : originalValue;
  };
  

  useEffect(() => {
    if (currentUser) {
      loadFamilyData();
    }
  }, [currentUser]);

  // Add a new custom participant to Firestore
  const addCustomParticipant = async (name, role = FAMILY_ROLES.CHILD) => {
    try {
      let familyId = familyInfo?.familyId;
      
      // If no family exists, create one
      if (!familyId) {
        console.log('No family found, creating new family...');
        const createResult = await createFamily(
          currentUser.uid, 
          currentUser.email, 
          currentUser.displayName || currentUser.email.split('@')[0]
        );
        
        if (createResult.error) {
          console.error('Failed to create family:', createResult.error);
          setError('Failed to create family');
          return;
        }
        
        familyId = createResult.familyId;
        console.log('Family created with ID:', familyId);
        
        // Reload family info
        const updatedFamilyInfo = await getUserFamilyInfo(currentUser.uid);
        setFamilyInfo(updatedFamilyInfo);
      }

      const result = await addCustomParticipantToFamily(familyId, name, role);
      if (result.success) {
        console.log('Custom participant added successfully');
        // Refresh the participants list immediately
        await refreshParticipants();
        // Emit global event to update all components
        participantEvents.emit();
        // Also reload family data to update family state
        await loadFamilyData();
        setSuccess(`Added ${name} to family participants`);
        setTimeout(() => setSuccess(''), 3000);
        return result.participant.id;
      } else {
        console.error('Failed to add custom participant:', result.error);
        setError(result.error || 'Failed to add participant');
      }
    } catch (error) {
      console.error('Error adding custom participant:', error);
      setError('Failed to add participant');
    }
  };

  // Remove a custom participant from Firestore
  const removeCustomParticipant = async (participantId) => {
    if (!familyInfo?.familyId) {
      console.error('No family ID found');
      return;
    }

    try {
      const result = await removeCustomParticipantFromFamily(familyInfo.familyId, participantId);
      if (result.success) {
        console.log('Custom participant removed successfully');
        // Refresh the participants list immediately
        refreshParticipants();
        // Emit global event to update all components
        participantEvents.emit();
        setSuccess('Participant removed successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        console.error('Failed to remove custom participant:', result.error);
        setError('Failed to remove participant');
      }
    } catch (error) {
      console.error('Error removing custom participant:', error);
      setError('Failed to remove participant');
    }
  };

  // Remove family member permanently
  const removeFamilyMemberPermanently = async (participantId) => {
    if (!familyInfo?.familyId) {
      console.error('No family ID found');
      return;
    }

    try {
      const result = await removeFamilyMember(familyInfo.familyId, participantId);
      if (result.success) {
        // Refresh the participants list immediately
        refreshParticipants();
        // Emit global event to update all components
        participantEvents.emit();
        loadFamilyData(); // Reload family data to update the UI
        setSuccess('Family member removed successfully');
        setTimeout(() => setSuccess(''), 3000);
        console.log('Family member removed successfully');
      } else {
        console.error('Failed to remove family member:', result.error);
        setError('Failed to remove family member');
      }
    } catch (error) {
      console.error('Error removing family member:', error);
      setError('Failed to remove family member');
    }
  };

  // Handle remove button click
  const handleRemoveClick = async (participant) => {
    if (participant.custom) {
      // Remove custom participant immediately
      await removeCustomParticipant(participant.id);
    } else {
      // Show confirmation for family members
      setMemberToRemove(participant);
      setShowRemoveConfirmation(true);
    }
  };

  // Confirm family member removal
  const confirmRemoveMember = async () => {
    if (memberToRemove) {
      await removeFamilyMemberPermanently(memberToRemove.id);
      setShowRemoveConfirmation(false);
      setMemberToRemove(null);
    }
  };

  // Toggle user expansion
  const toggleUserExpansion = (userId) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const loadFamilyData = async () => {
    try {
      setLoading(true);
      const userFamilyInfo = await getUserFamilyInfo(currentUser.uid);
      console.log('👨‍👩‍👧‍👦 loadFamilyData - userFamilyInfo:', userFamilyInfo);
      setFamilyInfo(userFamilyInfo);
      
      if (userFamilyInfo?.familyId) {
        const members = await getFamilyMembers(userFamilyInfo.familyId);
        console.log('👥 loadFamilyData - family members:', members);
        setFamilyMembers(members);
      } else {
        console.log('❌ No familyId found in userFamilyInfo');
        setFamilyMembers([]);
      }
      
      // Get invitations for the current user (received and sent)
      const [receivedInvitations, sentInvitations] = await Promise.all([
        getFamilyInvitations(currentUser.email),
        getSentInvitations(currentUser.uid)
      ]);
      console.log('📨 Invitations:', { receivedInvitations, sentInvitations });
      setPendingInvitations(receivedInvitations);
      setSentInvitations(sentInvitations);
    } catch (error) {
      console.error('Error loading family data:', error);
      setError('Failed to load family information');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail || !inviteName) {
      setError('Please fill in all fields');
      return;
    }

    setIsInviting(true);
    setError('');
    
    try {
      let familyId = familyInfo?.familyId;
      
      if (!familyId) {
        console.log('Creating new family...');
        const createResult = await createFamily(
          currentUser.uid, 
          currentUser.email, 
          currentUser.displayName || currentUser.email.split('@')[0]
        );
        if (createResult.error) {
          setError(createResult.error);
          return;
        }
        familyId = createResult.familyId;
        console.log('Family created with ID:', familyId);
        
        // Reload family info
        const updatedFamilyInfo = await getUserFamilyInfo(currentUser.uid);
        setFamilyInfo(updatedFamilyInfo);
      }
      
      await sendFamilyInvitation(
        currentUser.uid,
        currentUser.email,
        currentUser.displayName || currentUser.email.split('@')[0],
        inviteEmail,
        inviteName,
        inviteRole
      );
      
      setSuccess(`Invitation sent to ${inviteName} (${inviteEmail})`);
      setInviteEmail('');
      setInviteName('');
      setInviteRole(FAMILY_ROLES.CHILD);
      
      // Reload data
      loadFamilyData();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error sending invitation:', error);
      setError(error.message || 'Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const handleAcceptInvitation = async (invitationId) => {
    try {
      const result = await acceptFamilyInvitation(
        invitationId, 
        currentUser.uid,
        currentUser.email,
        currentUser.displayName || currentUser.email.split('@')[0]
      );
      
      if (result.error) {
        setError(result.error);
        return;
      }
      
      setSuccess('Family invitation accepted!');
      loadFamilyData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error accepting invitation:', error);
      setError('Failed to accept invitation');
    }
  };

  const handleDeclineInvitation = async (invitationId) => {
    try {
      const result = await declineFamilyInvitation(invitationId);
      
      if (result.error) {
        setError(result.error);
        return;
      }
      
      setSuccess('Family invitation declined');
      loadFamilyData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error declining invitation:', error);
      setError('Failed to decline invitation');
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case FAMILY_ROLES.PARENT: return 'Parent';
      case FAMILY_ROLES.CHILD: return 'Child';
      case FAMILY_ROLES.NANNY: return 'Nanny';
      case FAMILY_ROLES.DRIVER: return 'Driver';
      default: return role;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case FAMILY_ROLES.PARENT: return '#007bff';
      case FAMILY_ROLES.CHILD: return '#28a745';
      case FAMILY_ROLES.NANNY: return '#ffc107';
      case FAMILY_ROLES.DRIVER: return '#6f42c1';
      default: return '#6c757d';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ffc107';
      case 'accepted': return '#28a745';
      case 'declined': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusDisplayName = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'accepted': return 'Accepted';
      case 'declined': return 'Declined';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div>Loading family information...</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      padding: '20px',
      paddingTop: '80px'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '30px',
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
        }}>
          <button
            onClick={handleBack}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              marginRight: '15px',
              padding: '8px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0,0,0,0.05)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          
          <h1 style={{
            margin: 0,
            fontSize: '1.1rem',
            fontWeight: '600',
            color: '#333'
          }}>Family Setup</h1>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}
        
        {success && (
          <div style={{
            backgroundColor: '#d4edda',
            color: '#155724',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '0.9rem'
          }}>
            {success}
          </div>
        )}

        {/* Pending Invitations */}
        {pendingInvitations.length > 0 && (
          <div style={{
            backgroundColor: 'white',
            padding: '25px',
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
            marginBottom: '20px'
          }}>
            <h2 style={{
              fontSize: '1.3rem',
              fontWeight: '600',
              marginBottom: '20px',
              color: '#333',
              borderBottom: '2px solid #f0f0f0',
              paddingBottom: '10px'
            }}>Pending Invitations</h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {pendingInvitations.map((invitation) => (
                <div key={invitation.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '15px',
                  backgroundColor: '#fff3cd',
                  borderRadius: '8px',
                  border: '1px solid #ffeaa7'
                }}>
                  <div>
                    <div style={{
                      fontSize: '0.9rem',
                      color: '#007bff',
                      fontWeight: '600',
                      marginBottom: '6px'
                    }}>
                      Family invitation from {invitation.senderName}
                    </div>
                    <div style={{
                      fontSize: '0.8rem',
                      color: '#666',
                      marginBottom: '4px'
                    }}>
                      {invitation.senderEmail}
                    </div>
                    <div style={{
                      fontSize: '0.8rem',
                      color: getRoleColor(invitation.role),
                      fontWeight: '500'
                    }}>
                      Role: {getRoleDisplayName(invitation.role)}
                    </div>
                  </div>
                  
                  {invitation.receiverEmail === currentUser.email && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleAcceptInvitation(invitation.id)}
                        style={{
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          cursor: 'pointer'
                        }}
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => handleDeclineInvitation(invitation.id)}
                        style={{
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          cursor: 'pointer'
                        }}
                      >
                        Decline
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Family Members */}
        <div style={{
          backgroundColor: 'white',
          padding: '25px',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
          marginBottom: '20px'
        }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '20px',
              borderBottom: '2px solid #f0f0f0',
              paddingBottom: '10px'
            }}>
              <h2 style={{
                fontSize: '1.3rem',
                fontWeight: '600',
                color: '#333',
                flex: 1,
                margin: 0
              }}>Family Members</h2>
              <button
                type="button"
                onClick={() => setShowParticipantEditor(true)}
                style={{
                  background: '#007bff',
                  border: '1px solid #007bff',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  marginLeft: '12px',
                  marginTop: '-10px',
                  transition: 'all 0.2s ease',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#0056b3';
                  e.target.style.borderColor = '#0056b3';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#007bff';
                  e.target.style.borderColor = '#007bff';
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <line x1="19" y1="8" x2="19" y2="14"></line>
                  <line x1="22" y1="11" x2="16" y2="11"></line>
                </svg>
                Add New User
              </button>
            </div>
            
{familyMembers.length > 0 || (familyParticipants && familyParticipants.some(p => p.custom)) || sentInvitations.some(inv => inv.status !== 'declined') ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {(() => {
                  // Create a consolidated list to avoid duplicates
                  const consolidatedMembers = [];
                  const processedNames = new Set();
                  const processedEmails = new Set();

                  // Priority 1: Family Members (accepted users)
                  familyMembers.forEach((member) => {
                    if (!processedEmails.has(member.email) && !processedNames.has(member.name.toLowerCase())) {
                      consolidatedMembers.push({
                        type: 'family',
                        data: member,
                        key: member.userId
                      });
                      processedEmails.add(member.email);
                      processedNames.add(member.name.toLowerCase());
                    }
                  });

                  // Priority 2: Sent Invitations (pending/accepted but not yet in family members)
                  sentInvitations.filter(inv => inv.status !== 'declined').forEach((invitation) => {
                    if (!processedEmails.has(invitation.receiverEmail) && !processedNames.has(invitation.receiverName.toLowerCase())) {
                      consolidatedMembers.push({
                        type: 'invitation',
                        data: invitation,
                        key: invitation.id
                      });
                      processedEmails.add(invitation.receiverEmail);
                      processedNames.add(invitation.receiverName.toLowerCase());
                    }
                  });

                  // Priority 3: Custom Participants (only show if not invited and not family members)
                  if (familyParticipants) {
                    familyParticipants.filter(p => p.custom).forEach((participant) => {
                      // Only show custom participants that haven't been invited and aren't family members
                      if (!participant.invited && !processedNames.has(participant.name.toLowerCase())) {
                        consolidatedMembers.push({
                          type: 'custom',
                          data: participant,
                          key: participant.id
                        });
                        processedNames.add(participant.name.toLowerCase());
                      }
                    });
                  }

                  return consolidatedMembers.map(({ type, data, key }) => {
                    if (type === 'family') {
                      const isExpanded = expandedUsers.has(data.userId);
                      return (
                        <div key={key} style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          border: '1px solid #e9ecef',
                          transition: 'all 0.2s ease'
                        }}>
                          {/* Clickable header */}
                          <div 
                            onClick={() => toggleUserExpansion(data.userId)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '12px',
                              cursor: 'pointer',
                              borderRadius: '8px'
                            }}
                          >
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{
                                  fontWeight: '500',
                                  color: '#333',
                                  marginBottom: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}>
                                  {data.name}
                                  {data.userId === currentUser.uid && ' (You)'}
                                  <div style={{
                                    backgroundColor: '#ffc107',
                                    color: '#333',
                                    padding: '4px 12px',
                                    borderRadius: '16px',
                                    fontSize: '0.8rem',
                                    fontWeight: '500'
                                  }}>
                                    {getRoleDisplayName(data.role)}
                                  </div>
                                </div>
                                <div style={{
                                  fontSize: '0.85rem',
                                  color: '#666'
                                }}>
                                  {data.email}
                                </div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              {/* Expand/Collapse icon */}
                              <div style={{
                                transition: 'transform 0.2s ease',
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                color: '#666'
                              }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                              </div>
                            </div>
                          </div>
                          
                          {/* Expanded content */}
                          {isExpanded && (
                            <div style={{
                              padding: '0 12px 12px 12px',
                              borderTop: '1px solid #e9ecef',
                              marginTop: '0'
                            }}>
                              {data.permissions && familyInfo?.familyId && (
                                <div style={{
                                  fontSize: '0.75rem',
                                  color: '#666',
                                  marginBottom: '12px',
                                  marginTop: '12px'
                                }}>
                                  {data.permissions.canViewAllEvents ? 'View All Events' : 'View Assigned Events'} • {data.permissions.canEditEvents ? 'Can Edit' : 'Cannot Edit'}
                                </div>
                              )}
                              
                              {(isParent || canEditEvents) && data.userId !== currentUser.uid && familyInfo?.familyId && (
                                <div style={{ marginTop: '15px' }}>
                                  {/* Permission Toggles */}
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '15px' }}>
                                    {/* View Permission */}
                                    <div style={{
                                      background: '#ffffff',
                                      border: `2px solid ${data.permissions?.canViewAllEvents ? '#2A9D8F' : '#e9ecef'}`,
                                      borderRadius: '8px',
                                      padding: '12px',
                                      transition: 'all 0.2s ease'
                                    }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ flex: 1 }}>
                                          <h4 style={{ 
                                            margin: '0 0 4px 0', 
                                            fontSize: '13px', 
                                            fontWeight: '600', 
                                            color: '#333' 
                                          }}>
                                            Event Visibility
                                          </h4>
                                          <p style={{ 
                                            margin: 0, 
                                            fontSize: '11px', 
                                            color: '#666',
                                            lineHeight: '1.3'
                                          }}>
                                            {data.permissions?.canViewAllEvents 
                                              ? "Full Calendar — They'll see everything in your family calendar" 
                                              : "Invite Only — They'll only see events where they're invited"
                                            }
                                          </p>
                                        </div>
                                        <label style={{
                                          position: 'relative',
                                          display: 'inline-block',
                                          width: '40px',
                                          height: '22px'
                                        }}>
                                          <input
                                            type="checkbox"
                                            checked={getCurrentPermission(data.userId, 'canViewAllEvents', data.permissions?.canViewAllEvents || false)}
                                            onChange={async (e) => {
                                              e.stopPropagation();
                                              const newValue = e.target.checked;
                                              
                                              // IMMEDIATELY update local state for instant UI feedback
                                              setLocalPermissions(prev => ({
                                                ...prev,
                                                [data.userId]: {
                                                  ...prev[data.userId],
                                                  canViewAllEvents: newValue
                                                }
                                              }));
                                              
                                              console.log('🔧 Toggle clicked, immediate local update:', {
                                                userId: data.userId,
                                                newValue,
                                                familyId: familyInfo?.familyId
                                              });
                                              
                                              if (!familyInfo?.familyId) {
                                                console.error('❌ No familyId found!');
                                                setError('No family found - cannot update permissions');
                                                // Revert local state
                                                setLocalPermissions(prev => ({
                                                  ...prev,
                                                  [data.userId]: {
                                                    ...prev[data.userId],
                                                    canViewAllEvents: !newValue
                                                  }
                                                }));
                                                return;
                                              }
                                              
                                              try {
                                                const { updateUserPermissions } = await import('../services/familyService');
                                                const newPermissions = {
                                                  ...data.permissions,
                                                  canViewAllEvents: newValue
                                                };
                                                
                                                const result = await updateUserPermissions(
                                                  familyInfo.familyId,
                                                  data.userId,
                                                  newPermissions
                                                );
                                                
                                                if (!result.success) {
                                                  console.error('❌ Permission update failed:', result.error);
                                                  setError(result.error || 'Failed to update permissions');
                                                  // Revert local state on failure
                                                  setLocalPermissions(prev => ({
                                                    ...prev,
                                                    [data.userId]: {
                                                      ...prev[data.userId],
                                                      canViewAllEvents: !newValue
                                                    }
                                                  }));
                                                }
                                              } catch (error) {
                                                console.error('💥 Error updating permissions:', error);
                                                setError('Failed to update permissions: ' + error.message);
                                              }
                                            }}
                                            style={{ opacity: 0, width: 0, height: 0 }}
                                          />
                                          <span style={{
                                            position: 'absolute',
                                            cursor: 'pointer',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            backgroundColor: getCurrentPermission(data.userId, 'canViewAllEvents', data.permissions?.canViewAllEvents) ? '#2A9D8F' : '#ddd',
                                            transition: 'all 0.3s ease',
                                            borderRadius: '22px'
                                          }}>
                                            <span style={{
                                              position: 'absolute',
                                              content: '""',
                                              height: '16px',
                                              width: '16px',
                                              left: getCurrentPermission(data.userId, 'canViewAllEvents', data.permissions?.canViewAllEvents) ? '21px' : '3px',
                                              bottom: '3px',
                                              backgroundColor: 'white',
                                              transition: 'all 0.3s ease',
                                              borderRadius: '50%',
                                              boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                            }} />
                                          </span>
                                        </label>
                                      </div>
                                    </div>

                                    {/* Edit Permission */}
                                    <div style={{
                                      background: '#ffffff',
                                      border: `2px solid ${data.permissions?.canEditEvents ? '#2A9D8F' : '#e9ecef'}`,
                                      borderRadius: '8px',
                                      padding: '12px',
                                      transition: 'all 0.2s ease'
                                    }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ flex: 1 }}>
                                          <h4 style={{ 
                                            margin: '0 0 4px 0', 
                                            fontSize: '13px', 
                                            fontWeight: '600', 
                                            color: '#333' 
                                          }}>
                                            Event Editing
                                          </h4>
                                          <p style={{ 
                                            margin: 0, 
                                            fontSize: '11px', 
                                            color: '#666',
                                            lineHeight: '1.3'
                                          }}>
                                            {data.permissions?.canEditEvents 
                                              ? "Can Edit" 
                                              : "View Only"
                                            }
                                          </p>
                                        </div>
                                        <label style={{
                                          position: 'relative',
                                          display: 'inline-block',
                                          width: '40px',
                                          height: '22px'
                                        }}>
                                          <input
                                            type="checkbox"
                                            checked={getCurrentPermission(data.userId, 'canEditEvents', data.permissions?.canEditEvents || false)}
                                            onChange={async (e) => {
                                              e.stopPropagation();
                                              const newValue = e.target.checked;
                                              
                                              // IMMEDIATELY update local state for instant UI feedback
                                              setLocalPermissions(prev => ({
                                                ...prev,
                                                [data.userId]: {
                                                  ...prev[data.userId],
                                                  canEditEvents: newValue
                                                }
                                              }));
                                              
                                              console.log('🔧 Edit toggle clicked, immediate local update:', {
                                                userId: data.userId,
                                                newValue,
                                                familyId: familyInfo?.familyId
                                              });
                                              
                                              if (!familyInfo?.familyId) {
                                                console.error('❌ No familyId found!');
                                                setError('No family found - cannot update permissions');
                                                // Revert local state
                                                setLocalPermissions(prev => ({
                                                  ...prev,
                                                  [data.userId]: {
                                                    ...prev[data.userId],
                                                    canEditEvents: !newValue
                                                  }
                                                }));
                                                return;
                                              }
                                              
                                              try {
                                                const { updateUserPermissions } = await import('../services/familyService');
                                                const newPermissions = {
                                                  ...data.permissions,
                                                  canEditEvents: newValue
                                                };
                                                
                                                const result = await updateUserPermissions(
                                                  familyInfo.familyId,
                                                  data.userId,
                                                  newPermissions
                                                );
                                                
                                                if (!result.success) {
                                                  console.error('❌ Edit permission update failed:', result.error);
                                                  setError(result.error || 'Failed to update permissions');
                                                  // Revert local state on failure
                                                  setLocalPermissions(prev => ({
                                                    ...prev,
                                                    [data.userId]: {
                                                      ...prev[data.userId],
                                                      canEditEvents: !newValue
                                                    }
                                                  }));
                                                }
                                              } catch (error) {
                                                console.error('💥 Error updating edit permissions:', error);
                                                setError('Failed to update permissions: ' + error.message);
                                              }
                                            }}
                                            style={{ opacity: 0, width: 0, height: 0 }}
                                          />
                                          <span style={{
                                            position: 'absolute',
                                            cursor: 'pointer',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            backgroundColor: getCurrentPermission(data.userId, 'canEditEvents', data.permissions?.canEditEvents) ? '#2A9D8F' : '#ddd',
                                            transition: 'all 0.3s ease',
                                            borderRadius: '22px'
                                          }}>
                                            <span style={{
                                              position: 'absolute',
                                              content: '""',
                                              height: '16px',
                                              width: '16px',
                                              left: getCurrentPermission(data.userId, 'canEditEvents', data.permissions?.canEditEvents) ? '21px' : '3px',
                                              bottom: '3px',
                                              backgroundColor: 'white',
                                              transition: 'all 0.3s ease',
                                              borderRadius: '50%',
                                              boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                            }} />
                                          </span>
                                        </label>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Remove Button */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveClick(data);
                                    }}
                                    style={{
                                      background: '#dc3545',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      padding: '6px 12px',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      fontWeight: '500',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="3 6 5 6 21 6"></polyline>
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                    </svg>
                                    Remove
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    } else if (type === 'invitation') {
                      return (
                        <div key={key} style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px',
                          backgroundColor: data.status === 'pending' ? '#fff3cd' : '#f8f9fa',
                          borderRadius: '8px',
                          border: `1px solid ${data.status === 'pending' ? '#ffeaa7' : '#e9ecef'}`
                        }}>
                          <div>
                            <div style={{
                              fontWeight: '500',
                              color: '#333',
                              marginBottom: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              {data.receiverName}
                              <div style={{
                                backgroundColor: '#ffc107',
                                color: '#333',
                                padding: '4px 12px',
                                borderRadius: '16px',
                                fontSize: '0.8rem',
                                fontWeight: '500'
                              }}>
                                {getRoleDisplayName(data.role)}
                              </div>
                              <span style={{
                                fontSize: '0.85rem',
                                color: data.status === 'pending' ? '#856404' : '#28a745',
                                fontWeight: '400'
                              }}>
                                ({data.status === 'pending' ? 'Pending' : 'Accepted'})
                              </span>
                            </div>
                            <div style={{
                              fontSize: '0.85rem',
                              color: '#666'
                            }}>
                              {data.receiverEmail}
                            </div>
                          </div>
                        </div>
                      );
                    } else if (type === 'custom') {
                      const isExpanded = expandedUsers.has(data.id);
                      return (
                        <div key={key} style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          border: '1px solid #e9ecef',
                          transition: 'all 0.2s ease'
                        }}>
                          {/* Clickable header */}
                          <div 
                            onClick={() => toggleUserExpansion(data.id)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '12px',
                              cursor: 'pointer',
                              borderRadius: '8px'
                            }}
                          >
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{
                                  fontWeight: '500',
                                  color: '#333',
                                  marginBottom: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}>
                                  {data.name}
                                  <div style={{
                                    backgroundColor: '#ffc107',
                                    color: '#333',
                                    padding: '4px 12px',
                                    borderRadius: '16px',
                                    fontSize: '0.8rem',
                                    fontWeight: '500'
                                  }}>
                                    {data.role ? getRoleDisplayName(data.role) : 'Custom'}
                                  </div>
                                  {(isParent || canEditEvents) && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setParticipantToConnect(data);
                                        setShowConnectModal(true);
                                      }}
                                      style={{
                                        background: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '4px 10px',
                                        fontSize: '11px',
                                        cursor: 'pointer',
                                        fontWeight: '500'
                                      }}
                                    >
                                      Share calendar
                                    </button>
                                  )}
                                </div>
                                <div style={{
                                  fontSize: '0.85rem',
                                  color: '#666'
                                }}>
                                  Custom participant
                                </div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              {/* Expand/Collapse icon */}
                              <div style={{
                                transition: 'transform 0.2s ease',
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                color: '#666'
                              }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                              </div>
                            </div>
                          </div>
                          
                          {/* Expanded content */}
                          {isExpanded && (
                            <div style={{
                              padding: '0 12px 12px 12px',
                              borderTop: '1px solid #e9ecef',
                              marginTop: '0'
                            }}>
                              {(isParent || canEditEvents) && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveClick(data);
                                    }}
                                    style={{
                                      background: '#dc3545',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      padding: '6px 12px',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      fontWeight: '500',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="3 6 5 6 21 6"></polyline>
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                    </svg>
                                    Remove
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }
                  });
                })()}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                color: '#666',
                padding: '20px',
                fontStyle: 'italic'
              }}>
                No family members or participants yet. Send an invitation or add custom participants to get started!
              </div>
            )}
          </div>


        {/* ADD NEW USER MODAL */}
        {showParticipantEditor && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '20px',
            paddingTop: '10vh'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)'
            }}>
              <div style={{
                padding: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #eee',
                backgroundColor: '#2A9D8F',
                color: 'white',
                borderRadius: '12px 12px 0 0'
              }}>
                <h3 style={{ fontSize: '18px', margin: 0, color: 'white' }}>Add New User</h3>
                <button 
                  onClick={() => setShowParticipantEditor(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: 'white',
                    padding: '0',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ×
                </button>
              </div>
              
              <div style={{ padding: '20px' }}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    marginBottom: '10px'
                  }}>Create a new participant that you can later connect to a real user</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '15px' }}>
                    <input
                      type="text"
                      placeholder="Enter name (e.g., John, Sarah, etc.)"
                      style={{
                        padding: '12px 15px',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                      onKeyPress={async (e) => {
                        if (e.key === 'Enter') {
                          const name = e.target.value.trim();
                          if (name) {
                            await addCustomParticipant(name, newParticipantRole);
                            e.target.value = '';
                            setShowParticipantEditor(false);
                          }
                        }
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#007bff'}
                      onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                      autoFocus
                    />
                    <select
                      value={newParticipantRole}
                      onChange={(e) => setNewParticipantRole(e.target.value)}
                      style={{
                        padding: '12px 40px 12px 15px',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '14px',
                        outline: 'none',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        appearance: 'none',
                        backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6,9 12,15 18,9\'%3e%3c/polyline%3e%3c/svg%3e")',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                        backgroundSize: '16px'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#007bff'}
                      onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                    >
                      <option value={FAMILY_ROLES.CHILD}>Child</option>
                      <option value={FAMILY_ROLES.PARENT}>Parent</option>
                      <option value={FAMILY_ROLES.NANNY}>Nanny</option>
                      <option value={FAMILY_ROLES.DRIVER}>Driver</option>
                    </select>
                  </div>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                  <button
                    type="button"
                    onClick={() => setShowParticipantEditor(false)}
                    style={{
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={async (e) => {
                      const container = e.target.closest('[style*="padding: 20px"]');
                      const input = container.querySelector('input');
                      const name = input.value.trim();
                      if (name) {
                        await addCustomParticipant(name, newParticipantRole);
                        input.value = '';
                        setShowParticipantEditor(false);
                      }
                    }}
                    style={{
                      background: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Add User
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CONFIRMATION DIALOG */}
        {showRemoveConfirmation && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)'
            }}>
              <div style={{
                padding: '20px',
                borderBottom: '1px solid #eee',
                backgroundColor: '#2A9D8F',
                color: 'white',
                borderRadius: '12px 12px 0 0'
              }}>
                <h3 style={{ fontSize: '18px', margin: 0 }}>Confirm Removal</h3>
              </div>
              
              <div style={{padding: '20px', textAlign: 'center'}}>
                <p style={{marginBottom: '20px', fontSize: '14px', lineHeight: '1.5', color: '#333'}}>
                  Are you sure you want to permanently remove <strong>{memberToRemove?.name}</strong> from your family? 
                  This will remove them from all family events and cannot be undone.
                </p>
                <div style={{display: 'flex', justifyContent: 'center', gap: '12px'}}>
                  <button
                    type="button"
                    onClick={confirmRemoveMember}
                    style={{
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Remove
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowRemoveConfirmation(false);
                      setMemberToRemove(null);
                    }}
                    style={{
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CONNECT TO USER MODAL */}
        {showConnectModal && participantToConnect && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '20px',
            paddingTop: '10vh'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)'
            }}>
              <div style={{
                padding: '20px',
                borderBottom: '1px solid #eee',
                backgroundColor: '#2A9D8F',
                color: 'white',
                borderRadius: '12px 12px 0 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h3 style={{ fontSize: '18px', margin: 0, color: 'white' }}>Connect {participantToConnect.name} to a User</h3>
                <button 
                  onClick={() => {
                    setShowConnectModal(false);
                    setParticipantToConnect(null);
                    setConnectModalPermissions({
                      canViewAllEvents: false,
                      canEditEvents: false
                    });
                    setShowParticipantEditor(true);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: 'white',
                    padding: '0',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ×
                </button>
              </div>
              
              <form onSubmit={async (e) => {
                e.preventDefault();
                const email = e.target.email.value;
                // Use the role from the participant that was set in the Edit Participants modal
                const role = participantToConnect.role || FAMILY_ROLES.CHILD;
                
                setIsInviting(true);
                setError('');
                
                try {
                  let familyId = familyInfo?.familyId;
                  
                  if (!familyId) {
                    console.log('Creating new family...');
                    const createResult = await createFamily(
                      currentUser.uid, 
                      currentUser.email, 
                      currentUser.displayName || currentUser.email.split('@')[0]
                    );
                    if (createResult.error) {
                      setError(createResult.error);
                      return;
                    }
                    familyId = createResult.familyId;
                    console.log('Family created with ID:', familyId);
                    
                    // Reload family info
                    const updatedFamilyInfo = await getUserFamilyInfo(currentUser.uid);
                    setFamilyInfo(updatedFamilyInfo);
                  }
                  
                  await sendFamilyInvitation(
                    currentUser.uid,
                    currentUser.email,
                    currentUser.displayName || currentUser.email.split('@')[0],
                    email,
                    participantToConnect.name,
                    role
                  );
                  
                  // Store permissions for when the invitation is accepted
                  // We'll store this in the family document for now as pendingPermissions
                  const familyRef = doc(db, 'families', familyId);
                  const familyDoc = await getDoc(familyRef);
                  const familyData = familyDoc.data();
                  const pendingPermissions = familyData.pendingPermissions || {};
                  
                  // Store permissions by email (since we don't have userId yet)
                  pendingPermissions[email.toLowerCase()] = connectModalPermissions;
                  
                  await updateDoc(familyRef, {
                    pendingPermissions: pendingPermissions
                  });
                  
                  setSuccess(`Invitation sent to connect ${participantToConnect.name} with ${email}`);
                  setShowConnectModal(false);
                  setParticipantToConnect(null);
                  setConnectModalPermissions({
                    canViewAllEvents: false,
                    canEditEvents: false
                  });
                  
                  // Mark the custom participant as invited instead of removing them
                  await markCustomParticipantAsInvited(familyId, participantToConnect.id, email);
                  
                  // Reload data
                  loadFamilyData();
                  
                  setTimeout(() => setSuccess(''), 3000);
                } catch (error) {
                  console.error('Error sending invitation:', error);
                  setError(error.message || 'Failed to send invitation');
                } finally {
                  setIsInviting(false);
                }
              }} style={{ padding: '20px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      color: '#666',
                      marginBottom: '6px',
                      fontWeight: '500'
                    }}>
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #e9ecef',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#007bff'}
                      onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                      placeholder="Enter their email"
                      required
                    />
                  </div>

                  {/* Permissions Section */}
                  <div style={{ marginTop: '20px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      color: '#666',
                      marginBottom: '15px',
                      fontWeight: '500'
                    }}>
                      Set Permissions for {participantToConnect.name}
                    </label>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      {/* View Permission */}
                      <div style={{
                        background: '#f8f9fa',
                        border: `2px solid ${connectModalPermissions.canViewAllEvents ? '#2A9D8F' : '#e9ecef'}`,
                        borderRadius: '8px',
                        padding: '15px',
                        transition: 'all 0.2s ease'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ flex: 1 }}>
                            <h4 style={{ 
                              margin: '0 0 4px 0', 
                              fontSize: '14px', 
                              fontWeight: '600', 
                              color: '#333' 
                            }}>
                              Event Visibility
                            </h4>
                            <p style={{ 
                              margin: 0, 
                              fontSize: '12px', 
                              color: '#666',
                              lineHeight: '1.3'
                            }}>
                              {connectModalPermissions.canViewAllEvents 
                                ? "Full Calendar — They'll see everything in your family calendar" 
                                : "Invite Only — They'll only see events where they're invited"
                              }
                            </p>
                          </div>
                          <label style={{
                            position: 'relative',
                            display: 'inline-block',
                            width: '44px',
                            height: '24px'
                          }}>
                            <input
                              type="checkbox"
                              checked={connectModalPermissions.canViewAllEvents}
                              onChange={(e) => {
                                setConnectModalPermissions(prev => ({
                                  ...prev,
                                  canViewAllEvents: e.target.checked
                                }));
                              }}
                              style={{ opacity: 0, width: 0, height: 0 }}
                            />
                            <span style={{
                              position: 'absolute',
                              cursor: 'pointer',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: connectModalPermissions.canViewAllEvents ? '#2A9D8F' : '#ddd',
                              transition: '0.3s',
                              borderRadius: '24px',
                              '::before': {
                                position: 'absolute',
                                content: '""',
                                height: '18px',
                                width: '18px',
                                left: connectModalPermissions.canViewAllEvents ? '23px' : '3px',
                                bottom: '3px',
                                backgroundColor: 'white',
                                transition: '0.3s',
                                borderRadius: '50%',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                              }
                            }}>
                              <span style={{
                                position: 'absolute',
                                content: '""',
                                height: '18px',
                                width: '18px',
                                left: connectModalPermissions.canViewAllEvents ? '23px' : '3px',
                                bottom: '3px',
                                backgroundColor: 'white',
                                transition: '0.3s',
                                borderRadius: '50%',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                              }} />
                            </span>
                          </label>
                        </div>
                      </div>

                      {/* Edit Permission */}
                      <div style={{
                        background: '#f8f9fa',
                        border: `2px solid ${connectModalPermissions.canEditEvents ? '#2A9D8F' : '#e9ecef'}`,
                        borderRadius: '8px',
                        padding: '15px',
                        transition: 'all 0.2s ease'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ flex: 1 }}>
                            <h4 style={{ 
                              margin: '0 0 4px 0', 
                              fontSize: '14px', 
                              fontWeight: '600', 
                              color: '#333' 
                            }}>
                              Event Editing
                            </h4>
                            <p style={{ 
                              margin: 0, 
                              fontSize: '12px', 
                              color: '#666',
                              lineHeight: '1.3'
                            }}>
                              {connectModalPermissions.canEditEvents 
                                ? "Can Edit" 
                                : "View Only"
                              }
                            </p>
                          </div>
                          <label style={{
                            position: 'relative',
                            display: 'inline-block',
                            width: '44px',
                            height: '24px'
                          }}>
                            <input
                              type="checkbox"
                              checked={connectModalPermissions.canEditEvents}
                              onChange={(e) => {
                                setConnectModalPermissions(prev => ({
                                  ...prev,
                                  canEditEvents: e.target.checked
                                }));
                              }}
                              style={{ opacity: 0, width: 0, height: 0 }}
                            />
                            <span style={{
                              position: 'absolute',
                              cursor: 'pointer',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: connectModalPermissions.canEditEvents ? '#2A9D8F' : '#ddd',
                              transition: '0.3s',
                              borderRadius: '24px'
                            }}>
                              <span style={{
                                position: 'absolute',
                                content: '""',
                                height: '18px',
                                width: '18px',
                                left: connectModalPermissions.canEditEvents ? '23px' : '3px',
                                bottom: '3px',
                                backgroundColor: 'white',
                                transition: '0.3s',
                                borderRadius: '50%',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                              }} />
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                    <button
                      type="submit"
                      disabled={isInviting}
                      style={{
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: isInviting ? 'not-allowed' : 'pointer',
                        opacity: isInviting ? 0.7 : 1,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {isInviting ? 'Sending...' : 'Send Invitation'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* PERMISSION EDITOR MODAL */}
        {showPermissionEditor && memberToEditPermissions && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '20px',
            paddingTop: '10vh'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)'
            }}>
              <div style={{
                padding: '20px',
                borderBottom: '1px solid #eee',
                backgroundColor: '#2A9D8F',
                color: 'white',
                borderRadius: '12px 12px 0 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h3 style={{ fontSize: '18px', margin: 0, color: 'white' }}>
                  Edit Permissions for {memberToEditPermissions.name}
                </h3>
                <button 
                  onClick={() => {
                    setShowPermissionEditor(false);
                    setMemberToEditPermissions(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: 'white',
                    padding: '0',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ×
                </button>
              </div>
              
              <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {/* View Permission */}
                  <div style={{
                    background: '#f8f9fa',
                    border: `2px solid ${memberToEditPermissions.permissions?.canViewAllEvents ? '#2A9D8F' : '#e9ecef'}`,
                    borderRadius: '8px',
                    padding: '15px',
                    transition: 'all 0.2s ease'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ 
                          margin: '0 0 4px 0', 
                          fontSize: '14px', 
                          fontWeight: '600', 
                          color: '#333' 
                        }}>
                          Event Visibility
                        </h4>
                        <p style={{ 
                          margin: 0, 
                          fontSize: '12px', 
                          color: '#666',
                          lineHeight: '1.3'
                        }}>
                          {memberToEditPermissions.permissions?.canViewAllEvents 
                            ? "Full Calendar — They'll see everything in your family calendar" 
                            : "Invite Only — They'll only see events where they're invited"
                          }
                        </p>
                      </div>
                      <label style={{
                        position: 'relative',
                        display: 'inline-block',
                        width: '44px',
                        height: '24px'
                      }}>
                        <input
                          type="checkbox"
                          checked={memberToEditPermissions.permissions?.canViewAllEvents || false}
                          onChange={(e) => {
                            setMemberToEditPermissions(prev => ({
                              ...prev,
                              permissions: {
                                ...prev.permissions,
                                canViewAllEvents: e.target.checked
                              }
                            }));
                          }}
                          style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span style={{
                          position: 'absolute',
                          cursor: 'pointer',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: memberToEditPermissions.permissions?.canViewAllEvents ? '#2A9D8F' : '#ddd',
                          transition: '0.3s',
                          borderRadius: '24px'
                        }}>
                          <span style={{
                            position: 'absolute',
                            content: '""',
                            height: '18px',
                            width: '18px',
                            left: memberToEditPermissions.permissions?.canViewAllEvents ? '23px' : '3px',
                            bottom: '3px',
                            backgroundColor: 'white',
                            transition: '0.3s',
                            borderRadius: '50%',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                          }} />
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Edit Permission */}
                  <div style={{
                    background: '#f8f9fa',
                    border: `2px solid ${memberToEditPermissions.permissions?.canEditEvents ? '#2A9D8F' : '#e9ecef'}`,
                    borderRadius: '8px',
                    padding: '15px',
                    transition: 'all 0.2s ease'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ 
                          margin: '0 0 4px 0', 
                          fontSize: '14px', 
                          fontWeight: '600', 
                          color: '#333' 
                        }}>
                          Event Editing
                        </h4>
                        <p style={{ 
                          margin: 0, 
                          fontSize: '12px', 
                          color: '#666',
                          lineHeight: '1.3'
                        }}>
                          {memberToEditPermissions.permissions?.canEditEvents 
                            ? "Can Edit" 
                            : "View Only"
                          }
                        </p>
                      </div>
                      <label style={{
                        position: 'relative',
                        display: 'inline-block',
                        width: '44px',
                        height: '24px'
                      }}>
                        <input
                          type="checkbox"
                          checked={memberToEditPermissions.permissions?.canEditEvents || false}
                          onChange={(e) => {
                            setMemberToEditPermissions(prev => ({
                              ...prev,
                              permissions: {
                                ...prev.permissions,
                                canEditEvents: e.target.checked
                              }
                            }));
                          }}
                          style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span style={{
                          position: 'absolute',
                          cursor: 'pointer',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: memberToEditPermissions.permissions?.canEditEvents ? '#2A9D8F' : '#ddd',
                          transition: '0.3s',
                          borderRadius: '24px'
                        }}>
                          <span style={{
                            position: 'absolute',
                            content: '""',
                            height: '18px',
                            width: '18px',
                            left: memberToEditPermissions.permissions?.canEditEvents ? '23px' : '3px',
                            bottom: '3px',
                            backgroundColor: 'white',
                            transition: '0.3s',
                            borderRadius: '50%',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                          }} />
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        // Import the updateUserPermissions function
                        const { updateUserPermissions } = await import('../services/familyService');
                        
                        const result = await updateUserPermissions(
                          familyInfo.familyId,
                          memberToEditPermissions.userId,
                          memberToEditPermissions.permissions
                        );
                        
                        if (result.success) {
                          setSuccess('Permissions updated successfully');
                          setTimeout(() => setSuccess(''), 3000);
                          // Reload family data to reflect changes
                          await loadFamilyData();
                          setShowPermissionEditor(false);
                          setMemberToEditPermissions(null);
                        } else {
                          setError(result.error || 'Failed to update permissions');
                        }
                      } catch (error) {
                        console.error('Error updating permissions:', error);
                        setError('Failed to update permissions');
                      }
                    }}
                    style={{
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FamilySharePage;